import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Receipt, Download, Search, Filter, RefreshCw, ChevronLeft, ChevronRight,
  CheckCircle2, XCircle, Clock, CreditCard, AlertCircle, Building2,
  TrendingUp, IndianRupee, Hash, Calendar, X, Printer, FileText,
  Eye, ChevronDown, ArrowLeft, MessageSquare,
} from 'lucide-react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { useNavigate } from 'react-router-dom';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';
import API_BASE_URL from '../Config/config';
import {
  COMPANY, BANK, SAC_CODE, computeGST, amountInWords,
  fmtINR as gstFmtINR, fmtNum, fmtInvoiceDate,
  INVOICE_DECLARATION, INVOICE_TERMS, renderTermsAndSignatory,
  EVOLVEX_LOGO_URL as LOGO_URL,
} from '../utils/gstInvoiceConfig';

/* ── helpers ─────────────────────────────────────────────────── */
const fmt = (n) => Number(n || 0).toLocaleString('en-IN');
const fmtINR = (n) => `₹${fmt(parseFloat(n || 0).toFixed(0))}`;
const fmtDate = (d) => d
  ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';
const fmtDateShort = (d) => d
  ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  : '—';

/* ── Online payment status config ────────────────────────────── */
const STATUS_META = {
  SUCCESS: { label: 'Success', cls: 'bg-green-50 text-green-700 border-green-200', dot: 'bg-green-500', Icon: CheckCircle2 },
  PENDING: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', Icon: Clock },
  FAILED:  { label: 'Failed',  cls: 'bg-red-50  text-red-700  border-red-200',   dot: 'bg-red-500',   Icon: XCircle },
};
const statusMeta = (s) => STATUS_META[(s || '').toUpperCase()] || STATUS_META.PENDING;

/* ── Enterprise status config ────────────────────────────────── */
const ENT_STATUS = {
  PENDING:                 { label: 'Pending',                color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', dot: '#F59E0B' },
  SUBMITTED:               { label: 'Submitted',              color: '#1E40AF', bg: '#EFF6FF', border: '#BFDBFE', dot: '#3B82F6' },
  RESUBMITTED:             { label: 'Resubmitted',            color: '#0369A1', bg: '#F0F9FF', border: '#BAE6FD', dot: '#0EA5E9' },
  UNDER_VERIFICATION:      { label: 'Under Verification',     color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', dot: '#F59E0B' },
  APPROVED:                { label: 'Approved',               color: '#065F46', bg: '#F0FDF4', border: '#BBF7D0', dot: '#10B981' },
  REJECTED:                { label: 'Rejected',               color: '#7F1D1D', bg: '#FEF2F2', border: '#FECACA', dot: '#EF4444' },
  CLARIFICATION_REQUESTED: { label: 'Clarification Needed',  color: '#7C2D12', bg: '#FFF7ED', border: '#FED7AA', dot: '#EA580C' },
  CONVERTED:               { label: 'Converted',              color: '#14532D', bg: '#F0FDF4', border: '#BBF7D0', dot: '#059669' },
};

const EntStatusBadge = ({ status }) => {
  const cfg = ENT_STATUS[status] || ENT_STATUS.PENDING;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: '3px 9px', borderRadius: '99px',
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: cfg.dot }} />
      {cfg.label}
    </span>
  );
};

const PLAN_LABEL = { GOLD_LAUNCH: 'Gold Launch', PLATINUM_GROWTH: 'Platinum Growth', TITANIUM_ELITE: 'Titanium Elite' };
const planLabel = (code, name) => name || PLAN_LABEL[code] || code || '—';

/* ── Skeleton ─────────────────────────────────────────────────── */
const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-100 rounded ${className}`} />
);

/* ── QR Code generator ─────────────────────────────────────────── */
async function generateQRDataURL(text) {
  try {
    const size = 200;
    const canvas = document.createElement('canvas');
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, size, size);
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(10, 10, 40, 40); ctx.fillStyle = '#ffffff'; ctx.fillRect(14, 14, 32, 32); ctx.fillStyle = '#1a1a1a'; ctx.fillRect(18, 18, 24, 24);
    ctx.fillRect(150, 10, 40, 40); ctx.fillStyle = '#ffffff'; ctx.fillRect(154, 14, 32, 32); ctx.fillStyle = '#1a1a1a'; ctx.fillRect(158, 18, 24, 24);
    ctx.fillRect(10, 150, 40, 40); ctx.fillStyle = '#ffffff'; ctx.fillRect(14, 154, 32, 32); ctx.fillStyle = '#1a1a1a'; ctx.fillRect(18, 158, 24, 24);
    let hash = 0;
    for (let i = 0; i < text.length; i++) hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
    const cellSize = 6, startX = 60, startY = 60, cols = 14, rows = 14;
    for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
      if ((hash >> ((r * cols + c) % 32)) & 1) { ctx.fillStyle = '#1a1a1a'; ctx.fillRect(startX + c * cellSize, startY + r * cellSize, cellSize - 1, cellSize - 1); }
    }
    ctx.fillStyle = '#4F46E5'; ctx.font = 'bold 9px monospace'; ctx.textAlign = 'center'; ctx.fillText('VERIFIED', size / 2, size - 8);
    return canvas.toDataURL('image/png');
  } catch { return null; }
}

async function fetchImageAsBase64(url) {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => { const r = new FileReader(); r.onloadend = () => resolve(r.result); r.onerror = () => resolve(null); r.readAsDataURL(blob); });
  } catch { return null; }
}

/* ── Shared PDF helpers ────────────────────────────────────────── */
function buildTaxInvoicePDF({
  invoiceNumber, invoiceDate, customerName, customerEmail, customerState,
  description, qty, unitPrice, taxableValue, bankDetails, logoBase64, fileName,
}) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, H = 297, M = 14, CW = W - M * 2;
  const BRAND = [55, 48, 163], BRANDB = [79, 70, 229], DARK = [17, 24, 39], MID = [75, 85, 99];
  const BG = [249, 250, 251];
  const r = (...c) => doc.setTextColor(...c);
  const f = (...c) => doc.setFillColor(...c);
  const d = (...c) => doc.setDrawColor(...c);

  const gst = computeGST(taxableValue, customerState);
  const grandTotal = parseFloat((taxableValue + gst.totalTax).toFixed(2));
  const gstPctLabel = gst.type === 'CGST_SGST' ? '9%+9%' : '18%';
  const bank = bankDetails || BANK;

  // Watermark
  doc.saveGraphicsState();
  try { doc.setGState(new doc.GState({ opacity: 0.05 })); } catch (_) {}
  doc.setTextColor(170, 170, 170); doc.setFontSize(72); doc.setFont('helvetica', 'bold');
  doc.text('TAX INVOICE', W / 2, H / 2, { angle: 38, align: 'center', baseline: 'middle' });
  doc.restoreGraphicsState();

  // Header bar
  f(...BRAND); doc.rect(0, 0, W, 32, 'F');
  let hdrX = M;
  if (logoBase64) { doc.addImage(logoBase64, 'JPEG', M, 7, 16, 16); hdrX = M + 20; }
  r(255, 255, 255);
  doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.text(COMPANY.name, hdrX, 11);
  doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
  doc.text(`GSTIN: ${COMPANY.gstin}  |  PAN: ${COMPANY.pan}  |  State: ${COMPANY.state} (${COMPANY.stateCode})`, hdrX, 17);
  doc.text(COMPANY.fullAddress, hdrX, 22);
  doc.text(`${COMPANY.phone}  |  ${COMPANY.email}  |  ${COMPANY.website}`, hdrX, 27);
  // Right: title
  doc.setFontSize(15); doc.setFont('helvetica', 'bold'); doc.text('TAX INVOICE', W - M, 11, { align: 'right' });
  doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
  doc.text(`Invoice No: ${invoiceNumber}`, W - M, 18, { align: 'right' });
  doc.text(`Date: ${fmtInvoiceDate(invoiceDate)}`, W - M, 23, { align: 'right' });
  doc.text(`SAC Code: ${SAC_CODE}`, W - M, 28, { align: 'right' });

  let y = 38;
  const half = (CW - 4) / 2;

  const drawInfoBox = (x, bY, w, title, lines) => {
    doc.setFontSize(7.5);
    // First pass: wrap text and calculate total height
    const wrappedLines = [];
    lines.forEach((l) => {
      const text = typeof l === 'object' ? l.bold : l;
      const wrapped = doc.splitTextToSize(text, w - 8);
      wrappedLines.push({
        lines: wrapped,
        bold: typeof l === 'object',
        isBold: typeof l === 'object',
      });
    });
    const totalWrappedLines = wrappedLines.reduce((sum, item) => sum + item.lines.length, 0);
    const h = totalWrappedLines * 5 + 14;
    
    // Draw box
    f(...BG); d(220, 220, 220); doc.setLineWidth(0.3); doc.roundedRect(x, bY, w, h, 2, 2, 'FD');
    r(...BRANDB); doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.text(title, x + 4, bY + 5);
    d(220, 220, 220); doc.line(x + 4, bY + 6.5, x + w - 4, bY + 6.5);
    
    // Second pass: draw wrapped lines
    let yOffset = 11;
    wrappedLines.forEach((item) => {
      r(...(item.isBold ? DARK : MID)); doc.setFontSize(7.5); doc.setFont('helvetica', item.isBold ? 'bold' : 'normal');
      item.lines.forEach((line) => {
        doc.text(line, x + 4, bY + yOffset);
        yOffset += 5;
      });
    });
    
    return h;
  };

  const billLines = [
    { bold: customerName || '—' },
    `Email: ${customerEmail || '—'}`,
    `State: ${customerState || '—'}`,
  ];
  const suppLines = [
    { bold: COMPANY.name },
    `GSTIN: ${COMPANY.gstin}`,
    `PAN: ${COMPANY.pan}`,
    `State: ${COMPANY.state} (${COMPANY.stateCode})`,
    COMPANY.fullAddress,
  ];
  const bh = Math.max(billLines.length, suppLines.length) * 5 + 14;
  drawInfoBox(M, y, half, 'BILL TO (RECIPIENT)', billLines);
  drawInfoBox(M + half + 4, y, half, 'SUPPLIER (SELLER)', suppLines);
  y += bh + 6;

  // jsPDF helvetica does not support ₹ — use Rs. throughout PDF rendering
  const pdfINR = (n) => 'Rs. ' + fmtNum((parseFloat(n) || 0).toFixed(2));

  // Items table (8 cols, sum = 182mm)
  const cw = [50, 16, 18, 22, 26, 12, 16, 22];
  const hdrs = ['Description', 'SAC\nCode', 'Qty\n(Credits)', 'Unit\nPrice (Rs.)', 'Taxable\nValue (Rs.)', 'GST\n%', 'GST\nAmt (Rs.)', 'Total\n(Rs.)'];
  f(...BRAND); doc.rect(M, y, CW, 10, 'F');
  r(255, 255, 255); doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
  let cx = M;
  hdrs.forEach((h2, i) => {
    const lines2 = h2.split('\n');
    const ax = i > 0 ? cx + cw[i] - 2 : cx + 2;
    const al = i > 0 ? 'right' : 'left';
    if (lines2.length > 1) { doc.text(lines2[0], ax, y + 3.5, { align: al }); doc.text(lines2[1], ax, y + 7.5, { align: al }); }
    else doc.text(lines2[0], ax, y + 5.5, { align: al });
    cx += cw[i];
  });
  y += 10;

  // Data row
  f(255, 255, 255); doc.rect(M, y, CW, 8, 'F');
  d(210, 210, 210); doc.setLineWidth(0.2); doc.rect(M, y, CW, 8, 'D');
  r(...DARK); doc.setFontSize(7.5); doc.setFont('helvetica', 'normal');
  // Clip description to first line so it never overflows its column
  const descClipped = doc.splitTextToSize(description, cw[0] - 4)[0];
  const row = [
    descClipped,
    SAC_CODE,
    String(qty),
    fmtNum(unitPrice.toFixed(2)),
    fmtNum(taxableValue.toFixed(2)),
    gstPctLabel,
    fmtNum(gst.totalTax.toFixed(2)),
    fmtNum(grandTotal.toFixed(2)),
  ];
  cx = M;
  row.forEach((v, i) => {
    const al = i > 0 ? 'right' : 'left';
    doc.text(v, i > 0 ? cx + cw[i] - 2 : cx + 2, y + 5, { align: al });
    cx += cw[i];
  });
  y += 8;

  // Subtotal row
  f(...BG); doc.rect(M, y, CW, 7, 'F');
  d(210, 210, 210); doc.rect(M, y, CW, 7, 'D');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5);
  r(...MID); doc.text('Subtotal', M + 2, y + 4.5);
  const tvX = M + cw[0] + cw[1] + cw[2] + cw[3] + cw[4] - 2;
  const gaX = tvX + cw[5] + cw[6] - 2;
  const gtX2 = gaX + cw[7] - 2;
  r(...DARK);
  doc.text(fmtNum(taxableValue.toFixed(2)), tvX, y + 4.5, { align: 'right' });
  doc.text(fmtNum(gst.totalTax.toFixed(2)), gaX, y + 4.5, { align: 'right' });
  doc.text(fmtNum(grandTotal.toFixed(2)), gtX2, y + 4.5, { align: 'right' });
  y += 7 + 5;

  // GST Summary box
  const sumW = 84, sumX = M + CW - sumW;
  const sumRows = gst.type === 'CGST_SGST'
    ? [['Taxable Value', pdfINR(taxableValue)], ['CGST @ 9%', pdfINR(gst.cgst)], ['SGST @ 9%', pdfINR(gst.sgst)]]
    : [['Taxable Value', pdfINR(taxableValue)], ['IGST @ 18%', pdfINR(gst.igst)]];
  const sumH = sumRows.length * 6 + 20;
  f(...BG); d(220, 220, 220); doc.setLineWidth(0.3); doc.roundedRect(sumX, y, sumW, sumH, 2, 2, 'FD');
  r(...BRANDB); doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.text('GST SUMMARY', sumX + 4, y + 5);
  d(220, 220, 220); doc.line(sumX + 4, y + 6.5, sumX + sumW - 4, y + 6.5);
  sumRows.forEach(([lbl, val], i) => {
    const ry = y + 11 + i * 6;
    r(...MID); doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.text(lbl, sumX + 4, ry);
    r(...DARK); doc.text(val, sumX + sumW - 4, ry, { align: 'right' });
  });
  const gtRowY = y + 11 + sumRows.length * 6 + 1;
  f(...BRAND); doc.roundedRect(sumX, gtRowY, sumW, 8, 0, 0, 'F');
  r(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
  doc.text('Grand Total', sumX + 4, gtRowY + 5.5);
  doc.text(pdfINR(grandTotal), sumX + sumW - 4, gtRowY + 5.5, { align: 'right' });
  y += sumH + 6;

  // Amount in words
  f(255, 251, 235); d(253, 230, 138); doc.setLineWidth(0.3); doc.roundedRect(M, y, CW, 10, 2, 2, 'FD');
  r(146, 64, 14); doc.setFontSize(7); doc.setFont('helvetica', 'bold'); doc.text('Amount in Words:', M + 4, y + 4.5);
  r(...DARK); doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
  const wordsText = amountInWords(grandTotal);
  const splitWords = doc.splitTextToSize(wordsText, CW - 46);
  doc.text(splitWords, M + 42, y + 4.5);
  y += 16;

  // Footer helper — defined early so it can be used on any page
  const drawInvoiceFooter = (d2) => {
    d2.setFillColor(243, 244, 246);
    d2.rect(0, H - 16, W, 16, 'F');
    d2.setTextColor(75, 85, 99);
    d2.setFontSize(6.5); d2.setFont('helvetica', 'normal');
    d2.text('This is a computer-generated invoice. Subject to Vijayawada jurisdiction.', W / 2, H - 10, { align: 'center' });
    d2.setTextColor(79, 70, 229); d2.setFont('helvetica', 'bold');
    d2.text(`${COMPANY.email}  |  ${COMPANY.phone}  |  ${COMPANY.website}`, W / 2, H - 5, { align: 'center' });
  };

  // Bank Details (full width)
  const bankLines = [
    { bold: 'Bank Transfer Details — NEFT / RTGS / IMPS' },
    `A/C Name : ${bank.accountName || COMPANY.legalName}`,
    `A/C No   : ${bank.accountNumber || '—'}`,
    `IFSC     : ${bank.ifsc || '—'}`,
    `Bank     : ${bank.bankName || '—'}, ${bank.branch || '—'}`,
    ...(bank.upi ? [`UPI      : ${bank.upi}`] : []),
  ];
  drawInfoBox(M, y, CW, 'BANK DETAILS', bankLines);

  // Page 1 complete — draw footer and force T&C onto Page 2
  drawInvoiceFooter(doc);
  doc.addPage();

  // Terms & Conditions + Declaration + Authorized Signatory on Page 2
  renderTermsAndSignatory(doc, M, {
    W, H, M, CW,
    BRAND, BRANDB, DARK, MID,
    terms:       INVOICE_TERMS,
    declaration: INVOICE_DECLARATION,
    companyName: COMPANY.name,
    footerFn:    drawInvoiceFooter,
  });

  // Footer on the final page
  drawInvoiceFooter(doc);
  doc.save(fileName);
}

/* ── GST Tax Invoice for Razorpay (online) receipts ──────────── */
async function generateReceiptPDF(receipt) {
  const { receipt_number, receipt_date, transaction: tx, college, company } = receipt;
  const [logoBase64] = await Promise.all([fetchImageAsBase64(LOGO_URL)]);
  const base = parseFloat(tx.amount_inr || 0);
  const qty = parseInt(tx.credits_amount || 0, 10);
  const unitPrice = qty > 0 ? (base / qty) : parseFloat(tx.effective_price_per_credit || 0);
  buildTaxInvoicePDF({
    invoiceNumber: receipt_number,
    invoiceDate:   receipt_date,
    customerName:  college.name,
    customerEmail: college.email,
    customerState: college.state || '',
    description:   (tx.plan_name || planLabel(tx.plan_code, tx.plan_name) || 'Skill Genie Credits') + ' — Subscription',
    qty,
    unitPrice,
    taxableValue:  base,
    bankDetails:   company.bankDetails,
    logoBase64,
    fileName:      `TaxInvoice_${tx.razorpay_payment_id || tx.razorpay_order_id || 'receipt'}.pdf`,
  });
}

/* ── GST Tax Invoice for Enterprise (approved) receipts ─────── */
async function generateEnterpriseReceiptPDF(detail) {
  const ep = detail.purchase || {};
  const bank = detail.bankDetails || {};
  const credits = parseInt(ep.credits_purchased || 0, 10);
  const subtotal = parseFloat(ep.subtotal || 0);
  const unitP = credits > 0 ? subtotal / credits : 0;
  const logoBase64 = await fetchImageAsBase64(LOGO_URL);
  buildTaxInvoicePDF({
    invoiceNumber: ep.invoice_number || ep.quotation_number,
    invoiceDate:   ep.activated_at || ep.updated_at,
    customerName:  ep.college_name,
    customerEmail: ep.college_email,
    customerState: ep.college_state || '',
    description:   (ep.plan_name || ep.plan_code || 'Skill Genie Credits') + ' — Enterprise Subscription',
    qty:           credits,
    unitPrice:     unitP,
    taxableValue:  subtotal,
    bankDetails:   bank,
    logoBase64,
    fileName:      `TaxInvoice_${ep.invoice_number || ep.quotation_number || 'enterprise'}.pdf`,
  });
}

/* ── Enterprise Detail Modal ───────────────────────────────────── */
const EnterpriseDetailModal = ({ purchase, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dlInvoice, setDlInvoice] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const collegeId = user.id || user.userId;

  useEffect(() => {
    if (!purchase) return;
    const fetchDetail = async () => {
      setLoading(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/credits/enterprise/purchases/${purchase.id}?college_id=${collegeId}`
        );
        if (res.data.success) {
          setDetail(res.data);
        } else {
          setError(res.data.error || 'Failed to load details.');
        }
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to load details.');
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [purchase?.id, collegeId]);

  if (!purchase) return null;

  const ep = detail?.purchase || purchase;
  const audit = detail?.audit || [];

  const AUDIT_ICONS = {
    QUOTATION_GENERATED:   { bg: '#EFF6FF', icon: '📋' },
    QUOTATION_DOWNLOADED:  { bg: '#EFF6FF', icon: '📥' },
    UTR_SUBMITTED:         { bg: '#FFFBEB', icon: '💳' },
    PAYMENT_RESUBMITTED:   { bg: '#F0F9FF', icon: '🔄' },
    ADMIN_APPROVED:        { bg: '#F0FDF4', icon: '✅' },
    ADMIN_REJECTED:        { bg: '#FEF2F2', icon: '❌' },
    CLARIFICATION_REQUESTED: { bg: '#FFF7ED', icon: 'ℹ️' },
    ADMIN_VIEWED:          { bg: '#F8FAFC', icon: '👁️' },
  };

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '680px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(15,23,42,0.20)' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'linear-gradient(135deg,#7C3AED,#4C1D95)', borderRadius: '20px 20px 0 0' }}>
          <div>
            <p style={{ fontSize: '15px', fontWeight: 800, color: '#fff', margin: 0 }}>Enterprise Payment Details</p>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', margin: '2px 0 0' }}>{ep.quotation_number}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <EntStatusBadge status={ep.status} />
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', color: '#fff', padding: '6px', borderRadius: '8px', display: 'flex' }}>
              <X style={{ width: '16px', height: '16px' }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <RefreshCw style={{ width: '24px', height: '24px', color: '#7C3AED', margin: '0 auto 12px', display: 'block', animation: 'spin 1s linear infinite' }} />
              <p style={{ fontSize: '13px', color: '#64748B' }}>Loading details...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <AlertCircle style={{ width: '24px', height: '24px', color: '#DC2626', margin: '0 auto 12px', display: 'block' }} />
              <p style={{ fontSize: '13px', color: '#DC2626' }}>{error}</p>
            </div>
          ) : (
            <>
              {/* Quotation Info */}
              <section style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Quotation Information</p>
                <div style={{ background: '#F8FAFC', borderRadius: '12px', padding: '14px 16px' }}>
                  {[
                    { label: 'Quotation Number', value: ep.quotation_number },
                    { label: 'Plan', value: planLabel(ep.plan_code, ep.plan_name) },
                    { label: 'Credits', value: `${(ep.credits_purchased || 0).toLocaleString('en-IN')} credits` },
                    { label: 'Subtotal', value: fmtINR(ep.subtotal) },
                    { label: 'GST @ 18%', value: fmtINR(ep.gst) },
                    { label: 'Grand Total', value: fmtINR(ep.grand_total), bold: true, accent: '#7C3AED' },
                  ].map(({ label, value, bold, accent }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: bold ? '2px solid #E2E8F0' : '1px dashed #E2E8F0', marginTop: bold ? '4px' : 0 }}>
                      <span style={{ fontSize: '12px', color: '#64748B', fontWeight: bold ? 700 : 500 }}>{label}</span>
                      <span style={{ fontSize: bold ? '16px' : '13px', fontWeight: bold ? 800 : 600, color: accent || '#0F172A' }}>{value}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Payment Info */}
              <section style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Payment Information</p>
                <div style={{ background: '#F8FAFC', borderRadius: '12px', padding: '14px 16px' }}>
                  {[
                    { label: 'UTR Number', value: ep.utr_number ? <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#0F172A' }}>{ep.utr_number}</span> : <span style={{ color: '#94A3B8' }}>Not submitted yet</span> },
                    { label: 'Submitted Date', value: fmtDate(ep.submitted_at) },
                    { label: 'Submitted By', value: ep.submitted_by || '—' },
                  ].map(({ label, value }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F1F5F9' }}>
                      <span style={{ fontSize: '12px', color: '#64748B', fontWeight: 500 }}>{label}</span>
                      <span style={{ fontSize: '13px', fontWeight: 500 }}>{value}</span>
                    </div>
                  ))}
                  {ep.remarks && (
                    <div style={{ marginTop: '10px', padding: '10px 12px', background: '#fff', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                      <p style={{ fontSize: '11px', color: '#94A3B8', fontWeight: 600, margin: '0 0 4px' }}>REMARKS</p>
                      <p style={{ fontSize: '12px', color: '#475569', margin: 0, lineHeight: 1.5 }}>{ep.remarks}</p>
                    </div>
                  )}
                </div>
              </section>

              {/* Attachments */}
              {(ep.payment_proof_path || ep.purchase_order_path) && (
                <section style={{ marginBottom: '20px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 10px' }}>Attachments</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {ep.payment_proof_path && (
                      <a href={ep.payment_proof_path} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '10px', background: '#F8FAFC', border: '1px solid #E2E8F0', textDecoration: 'none', color: '#1E40AF' }}>
                        <FileText style={{ width: '14px', height: '14px' }} />
                        <span style={{ fontSize: '12px', fontWeight: 600, flex: 1 }}>Payment Proof</span>
                        <Download style={{ width: '13px', height: '13px' }} />
                      </a>
                    )}
                    {ep.purchase_order_path && (
                      <a href={ep.purchase_order_path} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '10px', background: '#F8FAFC', border: '1px solid #E2E8F0', textDecoration: 'none', color: '#1E40AF' }}>
                        <FileText style={{ width: '14px', height: '14px' }} />
                        <span style={{ fontSize: '12px', fontWeight: 600, flex: 1 }}>Purchase Order</span>
                        <Download style={{ width: '13px', height: '13px' }} />
                      </a>
                    )}
                  </div>
                </section>
              )}

              {/* Rejection / Clarification */}
              {ep.rejection_reason && (
                <section style={{ marginBottom: '20px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '12px', padding: '14px 16px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#7F1D1D', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 6px' }}>Rejection Reason</p>
                  <p style={{ fontSize: '13px', color: '#991B1B', margin: 0, lineHeight: 1.5 }}>{ep.rejection_reason}</p>
                </section>
              )}
              {ep.clarification_message && (() => {
                const clarifyEvent = [...audit].reverse().find(a => a.action === 'CLARIFICATION_REQUESTED');
                return (
                  <section style={{ marginBottom: '20px', background: '#FFF7ED', border: '2px solid #FED7AA', borderRadius: '14px', overflow: 'hidden' }}>
                    {/* Action Required header */}
                    <div style={{ background: 'linear-gradient(135deg,#EA580C,#C2410C)', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <AlertCircle style={{ width: '14px', height: '14px', color: '#fff', flexShrink: 0 }} />
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Action Required — Clarification Needed</span>
                    </div>
                    <div style={{ padding: '14px 16px' }}>
                      {clarifyEvent && (
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', paddingBottom: '10px', borderBottom: '1px dashed #FED7AA' }}>
                          <div>
                            <p style={{ fontSize: '10px', color: '#92400E', fontWeight: 700, textTransform: 'uppercase', margin: '0 0 2px' }}>Requested By</p>
                            <p style={{ fontSize: '12px', color: '#7C2D12', fontWeight: 600, margin: 0 }}>{clarifyEvent.performed_by}</p>
                          </div>
                          <div>
                            <p style={{ fontSize: '10px', color: '#92400E', fontWeight: 700, textTransform: 'uppercase', margin: '0 0 2px' }}>Requested On</p>
                            <p style={{ fontSize: '12px', color: '#7C2D12', fontWeight: 600, margin: 0 }}>{fmtDate(clarifyEvent.created_at)}</p>
                          </div>
                        </div>
                      )}
                      <p style={{ fontSize: '11px', fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px' }}>Message from Finance Team</p>
                      <p style={{ fontSize: '13px', color: '#7C2D12', margin: 0, lineHeight: 1.6, fontWeight: 500 }}>{ep.clarification_message}</p>
                      <p style={{ fontSize: '11px', color: '#92400E', fontWeight: 600, margin: '10px 0 0' }}>
                        Please update your UTR number, upload new payment proof or documents, add a response, then click "Update &amp; Resubmit" below.
                      </p>
                    </div>
                  </section>
                );
              })()}
              {ep.invoice_number && (
                <section style={{ marginBottom: '20px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '12px', padding: '14px 16px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#065F46', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 8px' }}>Invoice Generated</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ fontSize: '12px', color: '#15803D' }}>Invoice Number</span><span style={{ fontSize: '13px', fontWeight: 700, color: '#14532D' }}>{ep.invoice_number}</span></div>
                  {ep.verified_by && <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}><span style={{ fontSize: '12px', color: '#15803D' }}>Approved By</span><span style={{ fontSize: '12px', fontWeight: 600, color: '#14532D' }}>{ep.verified_by}</span></div>}
                  {ep.activated_at && <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}><span style={{ fontSize: '12px', color: '#15803D' }}>Activated At</span><span style={{ fontSize: '12px', color: '#14532D' }}>{fmtDate(ep.activated_at)}</span></div>}
                </section>
              )}

              {/* Status Timeline */}
              {audit.length > 0 && (
                <section style={{ marginBottom: '8px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 12px' }}>Status Timeline</p>
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {audit.map((entry, i) => {
                      const cfg = AUDIT_ICONS[entry.action] || { bg: '#F8FAFC', icon: '•' };
                      return (
                        <div key={entry.id || i} style={{ display: 'flex', gap: '10px', paddingBottom: '12px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: cfg.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', border: '2px solid #fff', boxShadow: '0 0 0 1px #E2E8F0' }}>{cfg.icon}</div>
                            {i < audit.length - 1 && <div style={{ width: '1px', flex: 1, background: '#E2E8F0', margin: '3px 0' }} />}
                          </div>
                          <div style={{ flex: 1, paddingTop: '4px' }}>
                            <p style={{ fontSize: '12px', fontWeight: 700, color: '#0F172A', margin: '0 0 2px' }}>{entry.action.replace(/_/g, ' ')}</p>
                            {entry.description && <p style={{ fontSize: '11px', color: '#64748B', margin: '0 0 2px', lineHeight: 1.4 }}>{entry.description}</p>}
                            <p style={{ fontSize: '10px', color: '#94A3B8', margin: 0 }}>{entry.performed_by} · {fmtDate(entry.created_at)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {ep.status === 'CLARIFICATION_REQUESTED' && (
            <button
              onClick={() => { onClose(); navigate(`/college/enterprise-purchase/${ep.id}`); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                padding: '10px 18px', borderRadius: '10px', border: 'none',
                background: 'linear-gradient(135deg,#EA580C,#C2410C)',
                fontSize: '13px', fontWeight: 700, color: '#fff', cursor: 'pointer',
                boxShadow: '0 3px 10px rgba(234,88,12,0.30)',
              }}
            >
              <MessageSquare style={{ width: '14px', height: '14px' }} />
              Update &amp; Resubmit
            </button>
          )}
          {ep.status === 'APPROVED' && ep.invoice_number && detail && (
            <button
              onClick={async () => {
                setDlInvoice(true);
                try { await generateEnterpriseReceiptPDF(detail); }
                catch (e) { alert('Failed to generate invoice. Please try again.'); }
                finally { setDlInvoice(false); }
              }}
              disabled={dlInvoice}
              style={{
                display: 'flex', alignItems: 'center', gap: '7px',
                padding: '10px 18px', borderRadius: '10px', border: 'none',
                background: dlInvoice ? '#94A3B8' : 'linear-gradient(135deg,#059669,#047857)',
                fontSize: '13px', fontWeight: 700, color: '#fff', cursor: dlInvoice ? 'not-allowed' : 'pointer',
                boxShadow: '0 3px 10px rgba(5,150,105,0.30)',
              }}
            >
              <Download style={{ width: '14px', height: '14px' }} />
              {dlInvoice ? 'Generating…' : 'Download Tax Invoice'}
            </button>
          )}
          <button onClick={onClose} style={{ marginLeft: 'auto', padding: '10px 20px', borderRadius: '10px', border: '1.5px solid #E2E8F0', background: '#fff', fontSize: '13px', fontWeight: 600, color: '#475569', cursor: 'pointer' }}>Close</button>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   Enterprise Payments Tab
════════════════════════════════════════════════════════════════ */
const EnterprisePaymentsTab = ({ collegeId }) => {
  const [purchases, setPurchases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const navigate = useNavigate();
  const LIMIT = 15;

  const handleDownloadEnterpriseInvoice = async (ep) => {
    setDownloadingInvoice(ep.id);
    try {
      const res = await axios.get(`${API_BASE_URL}/credits/enterprise/purchases/${ep.id}?college_id=${collegeId}`);
      if (res.data.success) await generateEnterpriseReceiptPDF(res.data);
      else alert(res.data.error || 'Failed to generate invoice.');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate invoice. Please try again.');
    } finally { setDownloadingInvoice(null); }
  };

  const fetchPurchases = useCallback(async (p = 1) => {
    if (!collegeId) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ college_id: collegeId, page: String(p), limit: String(LIMIT) });
      if (statusFilter) params.set('status', statusFilter);
      const res = await axios.get(`${API_BASE_URL}/credits/enterprise/purchases?${params}`);
      if (res.data.success) {
        setPurchases(res.data.purchases || []);
        setPagination(res.data.pagination || { total: 0, totalPages: 1 });
        // Compute simple stats from the data
        const all = res.data.purchases || [];
        setStats({
          total: res.data.pagination?.total || all.length,
          approved: all.filter(p => p.status === 'APPROVED').length,
          pending: all.filter(p => ['PENDING','SUBMITTED','UNDER_VERIFICATION','RESUBMITTED'].includes(p.status)).length,
          rejected: all.filter(p => p.status === 'REJECTED').length,
        });
      } else {
        setError(res.data.error || 'Failed to load enterprise payments.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load enterprise payments. Please try again.');
    } finally { setLoading(false); }
  }, [collegeId, statusFilter]);

  useEffect(() => { fetchPurchases(1); setPage(1); }, [fetchPurchases, statusFilter]);

  const filtered = search
    ? purchases.filter(p => (p.quotation_number || '').toLowerCase().includes(search.toLowerCase()) || (p.utr_number || '').toLowerCase().includes(search.toLowerCase()) || (p.plan_name || '').toLowerCase().includes(search.toLowerCase()))
    : purchases;

  const summaryCards = stats ? [
    { label: 'Total Payments',  value: stats.total,    color: '#4F46E5', bg: '#EEF2FF' },
    { label: 'Pending Review',  value: stats.pending,  color: '#D97706', bg: '#FFFBEB' },
    { label: 'Approved',        value: stats.approved, color: '#059669', bg: '#F0FDF4' },
    { label: 'Rejected',        value: stats.rejected, color: '#DC2626', bg: '#FEF2F2' },
  ] : [];

  return (
    <div>
      {/* Summary cards */}
      {!loading && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {summaryCards.map(({ label, value, color, bg }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
                <Building2 size={16} style={{ color }} />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider leading-tight">{label}</p>
                <p className="text-xl font-black text-gray-900">{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="text" placeholder="Search quotation #, UTR, plan..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 bg-gray-50"
            />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-400 min-w-[160px]">
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="SUBMITTED">Submitted</option>
            <option value="UNDER_VERIFICATION">Under Verification</option>
            <option value="RESUBMITTED">Resubmitted</option>
            <option value="APPROVED">Approved</option>
            <option value="REJECTED">Rejected</option>
            <option value="CLARIFICATION_REQUESTED">Clarification Needed</option>
          </select>
          <button onClick={() => fetchPurchases(page)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-all">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          {(search || statusFilter) && (
            <button onClick={() => { setSearch(''); setStatusFilter(''); }}
              className="flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700 px-2 py-2">
              <X size={13} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {error ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <AlertCircle size={28} className="text-red-400" />
            <p className="font-bold text-gray-700">Failed to load enterprise payments</p>
            <p className="text-sm text-gray-400">{error}</p>
            <button onClick={() => fetchPurchases(page)} className="px-6 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition">Retry</button>
          </div>
        ) : loading ? (
          <div className="p-4 space-y-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center">
              <Building2 size={32} className="text-purple-300" />
            </div>
            <p className="font-black text-gray-600">No Enterprise Payments Found</p>
            <p className="text-sm text-gray-400">
              {statusFilter || search ? 'Try adjusting your filters.' : 'Enterprise payment records will appear here after you submit payment for a quotation above ₹5,00,000.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Quotation #', 'Plan', 'Credits', 'Grand Total', 'UTR Number', 'Submitted Date', 'Status', 'Actions'].map(col => (
                      <th key={col} className="px-4 py-3 text-left text-[10px] font-black text-gray-500 uppercase tracking-wider whitespace-nowrap first:pl-5 last:pr-5">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filtered.map((ep, i) => (
                    <tr key={ep.id} style={ep.status === 'CLARIFICATION_REQUESTED' ? { background: '#FFF7ED', borderLeft: '3px solid #EA580C' } : {}} className={ep.status === 'CLARIFICATION_REQUESTED' ? 'transition-colors' : `hover:bg-indigo-50/30 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                      <td className="pl-5 pr-3 py-3.5 whitespace-nowrap">
                        <span className="text-xs font-mono font-bold text-indigo-700">{ep.quotation_number}</span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span className="text-xs font-bold text-gray-800">{planLabel(ep.plan_code, ep.plan_name)}</span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-black text-indigo-700">{(ep.credits_purchased || 0).toLocaleString('en-IN')}</span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap text-right">
                        <span className="text-sm font-black" style={{ color: '#4F46E5' }}>{fmtINR(ep.grand_total)}</span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        {ep.utr_number
                          ? <span className="text-xs font-mono font-bold bg-gray-100 px-2 py-0.5 rounded">{ep.utr_number}</span>
                          : <span className="text-[10px] text-gray-400 font-medium">Not submitted</span>
                        }
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <span className="text-xs text-gray-600">{ep.submitted_at ? fmtDate(ep.submitted_at) : <span className="text-gray-400">—</span>}</span>
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <EntStatusBadge status={ep.status} />
                          {ep.status === 'CLARIFICATION_REQUESTED' && (
                            <span style={{ fontSize: '9px', fontWeight: 800, color: '#EA580C', textTransform: 'uppercase', letterSpacing: '0.05em' }}>⚠ Action Required</span>
                          )}
                        </div>
                      </td>
                      <td className="pr-5 pl-4 py-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {ep.status === 'CLARIFICATION_REQUESTED' && (
                            <button
                              onClick={() => navigate(`/college/enterprise-purchase/${ep.id}`)}
                              style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#EA580C,#C2410C)', fontSize: '11px', fontWeight: 700, color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(234,88,12,0.30)' }}
                            >
                              <MessageSquare size={11} /> Update &amp; Resubmit
                            </button>
                          )}
                          {ep.status === 'APPROVED' && ep.invoice_number && (
                            <button
                              onClick={() => handleDownloadEnterpriseInvoice(ep)}
                              disabled={downloadingInvoice === ep.id}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '5px',
                                padding: '6px 12px', borderRadius: '8px', border: 'none',
                                background: downloadingInvoice === ep.id ? '#94A3B8' : 'linear-gradient(135deg,#059669,#047857)',
                                fontSize: '11px', fontWeight: 700, color: '#fff',
                                cursor: downloadingInvoice === ep.id ? 'not-allowed' : 'pointer',
                                whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(5,150,105,0.25)',
                              }}
                            >
                              <Download size={11} />
                              {downloadingInvoice === ep.id ? '…' : 'Invoice PDF'}
                            </button>
                          )}
                          <button
                            onClick={() => setSelectedPurchase(ep)}
                            className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 bg-white text-xs font-bold text-gray-600 rounded-lg hover:bg-indigo-50 hover:text-indigo-700 hover:border-indigo-200 transition-all"
                          >
                            <Eye size={12} /> View Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > LIMIT && (
              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-500 font-medium">
                  Showing <span className="font-bold text-gray-700">{((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, pagination.total)}</span> of <span className="font-bold text-gray-700">{pagination.total}</span>
                </p>
                <div className="flex items-center gap-2">
                  <button onClick={() => { setPage(p => Math.max(1, p - 1)); fetchPurchases(Math.max(1, page - 1)); }} disabled={page === 1}
                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                    <ChevronLeft size={15} />
                  </button>
                  <span className="text-xs font-bold text-gray-700">{page} / {pagination.totalPages}</span>
                  <button onClick={() => { setPage(p => Math.min(pagination.totalPages, p + 1)); fetchPurchases(Math.min(pagination.totalPages, page + 1)); }} disabled={page === pagination.totalPages}
                    className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
                    <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail modal */}
      {selectedPurchase && <EnterpriseDetailModal purchase={selectedPurchase} onClose={() => setSelectedPurchase(null)} />}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   Online Payments Tab (existing Razorpay transaction table)
════════════════════════════════════════════════════════════════ */
const OnlinePaymentsTab = ({ collegeId }) => {
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const LIMIT = 15;

  const fetchTransactions = useCallback(async (p = page) => {
    if (!collegeId) { setError('College ID not found. Please log in again.'); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ college_id: collegeId, page: String(p), limit: String(LIMIT), ...(search ? { search } : {}), ...(status ? { status } : {}), ...(fromDate ? { fromDate } : {}), ...(toDate ? { toDate } : {}) });
      const res = await axios.get(`${API_BASE_URL}/credits/purchase-transactions?${params}`);
      setTransactions(res.data.transactions || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 1 });
      setStats(res.data.stats || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load transactions. Please try again.');
    } finally { setLoading(false); }
  }, [collegeId, page, search, status, fromDate, toDate]);

  useEffect(() => { fetchTransactions(1); setPage(1); }, [search, status, fromDate, toDate]);
  useEffect(() => { fetchTransactions(page); }, [page]);

  const handleDownloadReceipt = async (tx) => {
    const txId = tx.razorpay_payment_id || tx.razorpay_order_id;
    setDownloading(txId);
    try {
      const res = await axios.get(`${API_BASE_URL}/credits/purchase-transactions/${txId}/receipt-data?college_id=${collegeId}`);
      await generateReceiptPDF(res.data.receipt);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to generate receipt. Please try again.');
    } finally { setDownloading(null); }
  };

  const clearFilters = () => { setSearch(''); setStatus(''); setFromDate(''); setToDate(''); setPage(1); };
  const hasFilters = search || status || fromDate || toDate;

  const summaryCards = stats ? [
    { label: 'Total Transactions', value: stats.total_count || 0,    icon: <Hash size={18}/>,         color: '#4F46E5', bg: '#EEF2FF' },
    { label: 'Total Credits',      value: fmt(stats.total_credits),   icon: <CreditCard size={18}/>,   color: '#7C3AED', bg: '#F5F3FF' },
    { label: 'Total Amount Paid',  value: `₹${fmt(parseFloat(stats.total_amount_paid || 0).toFixed(0))}`, icon: <IndianRupee size={18}/>, color: '#059669', bg: '#F0FDF4' },
    { label: 'Successful',         value: stats.success_count || 0,   icon: <CheckCircle2 size={18}/>, color: '#059669', bg: '#F0FDF4' },
    { label: 'Failed',             value: stats.failed_count || 0,    icon: <XCircle size={18}/>,      color: '#DC2626', bg: '#FEF2F2' },
  ] : [];

  return (
    <div>
      {loading && !stats ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
      ) : stats ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
          {summaryCards.map(({ label, value, icon, color, bg }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: bg, color }}>{icon}</div>
              <div className="min-w-0">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider leading-tight truncate">{label}</p>
                <p className="text-lg font-black text-gray-900 leading-tight">{value}</p>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input type="text" placeholder="Search Transaction ID or Order ID…" value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-2 text-sm rounded-xl border border-gray-200 focus:outline-none focus:border-indigo-400 focus:ring-1 focus:ring-indigo-200 bg-gray-50" />
          </div>
          <select value={status} onChange={e => setStatus(e.target.value)} className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-400 min-w-[140px]">
            <option value="">All Statuses</option><option value="SUCCESS">Success</option><option value="PENDING">Pending</option><option value="FAILED">Failed</option>
          </select>
          <div className="flex flex-col"><label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">From</label><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-400" /></div>
          <div className="flex flex-col"><label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">To</label><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="text-sm bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:border-indigo-400" /></div>
          <button onClick={() => fetchTransactions(page)} className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-all">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          {hasFilters && <button onClick={clearFilters} className="flex items-center gap-1 text-xs font-bold text-red-500 hover:text-red-700 px-2 py-2"><X size={13} /> Clear</button>}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {error ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <AlertCircle size={28} className="text-red-400" />
            <p className="font-bold text-gray-700">Failed to load transactions</p>
            <p className="text-sm text-gray-400">{error}</p>
            <button onClick={() => fetchTransactions(page)} className="px-6 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition">Retry</button>
          </div>
        ) : loading ? (
          <div className="p-4 space-y-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center"><Receipt size={32} className="text-indigo-300" /></div>
            <p className="font-black text-gray-600">No Online Transactions Found</p>
            <p className="text-sm text-gray-400">{hasFilters ? 'Try adjusting your search or filters.' : 'Online credit purchases via Razorpay will appear here.'}</p>
            {hasFilters && <button onClick={clearFilters} className="text-sm font-bold text-indigo-600 hover:underline">Clear Filters</button>}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead><tr className="bg-gray-50 border-b border-gray-100">
                  {['Transaction ID','Order ID','Purchase Date','Plan','Credits','Amount','GST (18%)','Total Paid','Payment Method','Status','Receipt'].map(col => (
                    <th key={col} className="px-4 py-3 text-left text-[10px] font-black text-gray-500 uppercase tracking-wider whitespace-nowrap first:pl-5 last:pr-5">{col}</th>
                  ))}
                </tr></thead>
                <tbody className="divide-y divide-gray-50">
                  {transactions.map((tx, i) => {
                    const meta = statusMeta(tx.payment_status);
                    const txId = tx.razorpay_payment_id || tx.razorpay_order_id || '—';
                    const isSuccess = (tx.payment_status || '').toUpperCase() === 'SUCCESS';
                    const isDownloading = downloading === (tx.razorpay_payment_id || tx.razorpay_order_id);
                    return (
                      <tr key={tx.id || i} className={`hover:bg-indigo-50/30 transition-colors ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}>
                        <td className="pl-5 pr-3 py-3.5 min-w-[150px]"><span className="text-xs font-mono font-bold text-indigo-700 truncate block max-w-[140px]" title={txId}>{txId !== '—' ? txId.slice(-12) : '—'}</span></td>
                        <td className="px-4 py-3.5 min-w-[150px]"><span className="text-xs font-mono text-gray-600 truncate block max-w-[140px]" title={tx.razorpay_order_id}>{(tx.razorpay_order_id || '—').slice(-12)}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap"><span className="text-xs text-gray-700">{fmtDate(tx.created_at)}</span></td>
                        <td className="px-4 py-3.5 min-w-[130px]"><span className="text-xs font-bold text-gray-800">{planLabel(tx.plan_code, tx.plan_name)}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap text-right"><span className="text-sm font-black text-indigo-700">{fmt(tx.credits_amount)}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap text-right"><span className="text-sm font-bold text-gray-700">₹{fmt(parseFloat(tx.amount_inr || 0).toFixed(0))}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap text-right"><span className="text-sm text-gray-500">₹{fmt(parseFloat(tx.gst_amount || 0).toFixed(0))}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap text-right"><span className="text-sm font-black text-gray-900">₹{fmt(parseFloat(tx.total_paid || 0).toFixed(0))}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap"><span className="text-xs text-gray-600">{tx.payment_method || 'Online'}</span></td>
                        <td className="px-4 py-3.5 whitespace-nowrap">
                          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black border ${meta.cls}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>{meta.label}
                          </div>
                        </td>
                        <td className="pr-5 pl-4 py-3.5 whitespace-nowrap">
                          {isSuccess ? (
                            <button onClick={() => handleDownloadReceipt(tx)} disabled={isDownloading} title="Download PDF Receipt"
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-bold rounded-lg hover:bg-indigo-700 transition-all disabled:opacity-60 disabled:cursor-wait shadow-sm shadow-indigo-200">
                              {isDownloading ? <><div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin"/>Generating…</> : <><Download size={12}/>PDF</>}
                            </button>
                          ) : <span className="text-[10px] text-gray-300 font-bold">N/A</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {pagination.total > 0 && (
              <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
                <p className="text-xs text-gray-500 font-medium">Showing <span className="font-bold text-gray-700">{((page - 1) * LIMIT) + 1}–{Math.min(page * LIMIT, pagination.total)}</span> of <span className="font-bold text-gray-700">{pagination.total}</span></p>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"><ChevronLeft size={15} /></button>
                  <span className="text-xs font-bold text-gray-700">{page} / {pagination.totalPages}</span>
                  <button onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={page === pagination.totalPages} className="p-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"><ChevronRight size={15} /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════
   Main Component
════════════════════════════════════════════════════════════════ */
const CollegeTransactionHistory = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const collegeId = user.id || user.userId;
  const [activeTab, setActiveTab] = useState('enterprise');

  const tabs = [
    { id: 'enterprise', label: 'Enterprise Payments', icon: <Building2 size={15} />, desc: 'Bank transfer payments' },
    { id: 'online',     label: 'Online Payments',     icon: <CreditCard size={15} />, desc: 'Razorpay transactions' },
  ];

  return (
    <div className="college-main-layout">
      <CollegeHeader />
      <div className="college-content-wrapper">
        <CollegeHomeSideBar />
        <main className="college-main-content">

          {/* Page Header */}
          <header className="mb-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="p-2.5 bg-gradient-to-br from-indigo-600 to-purple-700 rounded-xl text-white shadow-lg shadow-indigo-200">
                <Receipt size={22} />
              </div>
              <div>
                <h1 className="text-2xl font-black text-gray-900">Transaction History</h1>
                <p className="text-sm text-gray-500">All payment transactions for your institution</p>
              </div>
            </div>
          </header>

          {/* Tab Switcher */}
          <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-2xl w-fit">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                  activeTab === tab.id
                    ? 'bg-white text-indigo-700 shadow-sm shadow-indigo-100'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'enterprise' && <EnterprisePaymentsTab collegeId={collegeId} />}
          {activeTab === 'online' && <OnlinePaymentsTab collegeId={collegeId} />}

        </main>
      </div>
    </div>
  );
};

export default CollegeTransactionHistory;
